import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { PercentageType } from '../../../../../shared/constants/percentage.constants';
import { MasterSummaryPDModel, MasterSummaryQuestionModel, SurveyCutQuestionModel, SurveyVersionQuestionModel } from '../../../../../models/survey/survey.model';
import Utils from '../../../../../shared/util/Util';
import { CommunicationService } from '../../../../../shared/services/communication.service';

@Component({
  selector: 'app-percentage-icon',
  templateUrl: './percentage-icon.component.html',
  styleUrls: ['./percentage-icon.component.less']
})
export class PercentageIconComponent implements OnInit {
  showDropdownPercentage = false;
  private listening = false;
  private globalClick: any;
  @Input("surveyMasterSummaryPDModel") survMastSummPDModel: MasterSummaryPDModel;
  @Input("chartJ") chartJ: number;
  @Input("questionI") questionI: number;
  @Input("isCut") isCut: boolean;
  @Input() isCompareMode: boolean = false;
  @Input() isPercentageActivated: boolean = false;
  @Input() fromSmartAnalysis = false;
  @Input() isAllResponses: boolean;
  @Output() refToObject = new EventEmitter<PercentageIconComponent>();
  @Output() onSelectPercentage = new EventEmitter<boolean>();

  constructor(private communicationService: CommunicationService) { }

  ngOnInit() {
    this.refToObject.emit(this);
    if (this.fromSmartAnalysis) {
      this.communicationService.selectedPercentage
        .subscribe(
          (response) => {
            if (response.chartJ === this.chartJ && response.questionI === this.questionI && response.isCut === this.isCut) {
              this.selectedPercentage(response.percentageType);
            }
          }
        );
    }
  }

  displayDropdownPercentage(element) {
    this.showDropdownPercentage = !this.showDropdownPercentage;
    if (this.showDropdownPercentage && !this.isPercentageActivated) {
      this.globalClick = Observable
        .fromEvent(document, 'click')
        .delay(100)
        .do(() => {
          this.listening = true;
        }).subscribe((event: MouseEvent) => {
          this.onGlobalClick(element, event);
        });
    } else if(this.isPercentageActivated && !this.showDropdownPercentage) {
      this.listening = false;
      this.globalClick.unsubscribe();
    }else{
      this.resetAllDataTurnOffIcon();
      this.showDropdownPercentage = false;
      this.onSelectPercentage.emit(false);
      this.communicationService.percentageDiff.next({
        questionI: this.questionI
      });  
    }
  }

  onGlobalClick(element, event: MouseEvent) {
    if (event instanceof MouseEvent && this.listening === true) {
      if (!this.isDescendant(element.target, event.target)) {
        this.showDropdownPercentage = false;
        this.listening = false;
        this.globalClick.unsubscribe();
      }
    }
  }

  isDescendant(parent, child) {
    let node = child;
    while (node !== null) {
      if (node === parent) {
        return true;
      } else {
        node = node.parentNode;
      }
    }
    return false;
  }

  selectedPercentage(_percentageType: string) {
    this.applySelectedPercentage(_percentageType);
    this.onSelectPercentage.emit(true);
    this.communicationService.percentageDiff.next({
      questionI: this.questionI
    });
  }

  applySelectedPercentage(_percentageType: string){
    if(this.isCompareMode){
      let masterChart = this.obtainMasterChartObject();
      let questionIndex = this.isAllResponses ? this.chartJ : this.questionI;
      if(PercentageType.ABSOLUTE == _percentageType){
        if(this.survMastSummPDModel && this.survMastSummPDModel.listSurveyCutQuestion){
          this.calculateAllAbsolute(masterChart,this.survMastSummPDModel.listSurveyCutQuestion,questionIndex);
        }
        if(this.survMastSummPDModel && this.survMastSummPDModel.listSurveyVersionQuestion){
          this.calculateAllAbsolute(masterChart,this.survMastSummPDModel.listSurveyVersionQuestion,questionIndex);
        }
        if(this.survMastSummPDModel && this.survMastSummPDModel.listQuestionModel){
          this.calculateAllAbsoluteAllResponses(masterChart,this.survMastSummPDModel.listQuestionModel,questionIndex);
        }
        this.resetChart(masterChart);
        masterChart.typePercentageSelected = PercentageType.ABSOLUTE;
        this.changeChartSelection(false);
      }else if(PercentageType.RELATIVE == _percentageType) {
        if(this.survMastSummPDModel && this.survMastSummPDModel.listSurveyCutQuestion){
          this.calculateAllRelative(masterChart,this.survMastSummPDModel.listSurveyCutQuestion,questionIndex);
        }
        if (this.survMastSummPDModel && this.survMastSummPDModel.listSurveyVersionQuestion){
          this.calculateAllRelative(masterChart,this.survMastSummPDModel.listSurveyVersionQuestion,questionIndex);
        }
        if(this.survMastSummPDModel && this.survMastSummPDModel.listQuestionModel){
          this.calculateAllRelativeAllResponses(masterChart,this.survMastSummPDModel.listQuestionModel,questionIndex);
        }
        this.resetChart(masterChart);
        masterChart.typePercentageSelected = PercentageType.RELATIVE;
        this.changeChartSelection(false);
      }
    }
  }

  get PercentageType(): typeof PercentageType {
    return PercentageType
  }

  resetAllChart(){
    this.resetAllDataTurnOffIcon();
  }

  //This function return the chose chart based on the input parameters chartJ and questionI and if isCutQuestion or not
  private obtainMasterChartObject(): MasterSummaryQuestionModel{
    if(this.isAllResponses){
      return this.survMastSummPDModel.listQuestionModel[this.chartJ];
    }else{
      if(!this.isCut){
        return this.survMastSummPDModel.listSurveyVersionQuestion[this.chartJ].listQuestionModel[this.questionI];
      }else{
        return this.survMastSummPDModel.listSurveyCutQuestion[this.chartJ].listQuestionModel[this.questionI];
      }
    }
  }

  /*This function will iterate over all chart and it will call the function to calculate the absolute percentage difference
   *It will also reset the masterChart to not show the percentage difference in that chart
   */
  private calculateAllAbsolute(masterChart: MasterSummaryQuestionModel,listQuestion: SurveyVersionQuestionModel[] | SurveyCutQuestionModel[],questionIndex: number){
    let i: number;
    let auxChart: MasterSummaryQuestionModel;
    for(i=0;i<listQuestion.length;i++){
      auxChart = listQuestion[i].listQuestionModel[questionIndex];
      this.calculateAbsolute(masterChart,auxChart);
    }
  }

  private calculateAllAbsoluteAllResponses(masterChart: MasterSummaryQuestionModel,listQuestion: MasterSummaryQuestionModel[],questionIndex: number){
    this.calculateAbsolute(masterChart,listQuestion[questionIndex]);
  }

  //This function will apply this formula PercentageSibling - PercentageMaster to each question in the charts
  private calculateAbsolute(masterChart: MasterSummaryQuestionModel,siblingChart: MasterSummaryQuestionModel){
    if(masterChart && siblingChart && masterChart.listAnswerModel && siblingChart.listAnswerModel){
      let i: number;
      siblingChart.typePercentageSelected = PercentageType.ABSOLUTE;
      if(masterChart.listAnswerModel.length > 0 && siblingChart.listAnswerModel.length > 0){
        for(i=0 ; i<masterChart.listAnswerModel.length && i<siblingChart.listAnswerModel.length ; i++){
          siblingChart.listAnswerModel[i].answerDiffPercShow = true;
          let diffPercentage = siblingChart.listAnswerModel[i].answerPercentage - masterChart.listAnswerModel[i].answerPercentage;
          siblingChart.listAnswerModel[i].answerDiffPercentage = Utils.roundNumber(diffPercentage,1);
        }
      }else if(masterChart.nizPitanja && siblingChart.nizPitanja &&masterChart.nizPitanja.length > 0 && siblingChart.nizPitanja.length > 0){
        for(i=0 ; i<masterChart.nizPitanja.length && i<siblingChart.nizPitanja.length ; i++){
          siblingChart.nizPitanja[i].answerDiffPercShow = true;
          let diffPercentage = siblingChart.nizPitanja[i].answerPercentage - masterChart.nizPitanja[i].answerPercentage;
          siblingChart.nizPitanja[i].answerDiffPercentage = Utils.roundNumber(diffPercentage,1);
        }
      }
    }
  }

  /*This function will iterate over all chart and it will call the function to calculate the relative percentage difference
   *It will also reset the masterChart to not show the percentage difference in that chart
   */
   private calculateAllRelative(masterChart: MasterSummaryQuestionModel,listQuestion: SurveyVersionQuestionModel[] | SurveyCutQuestionModel[],questionIndex: number){
    let i: number;
    let auxChart: MasterSummaryQuestionModel;
    for(i=0;i<listQuestion.length;i++){
      auxChart = listQuestion[i].listQuestionModel[questionIndex];
      this.calculateRelative(masterChart,auxChart);
    }
  }

  private calculateAllRelativeAllResponses(masterChart: MasterSummaryQuestionModel,listQuestion: MasterSummaryQuestionModel[],questionIndex: number){
    this.calculateRelative(masterChart,listQuestion[questionIndex]);
  }

  //This function will apply this formula ((PercentageSibling - PercentageMaster)/PercentageMaster)*100 to each question in the charts
  private calculateRelative(masterChart: MasterSummaryQuestionModel,siblingChart: MasterSummaryQuestionModel){
    if(masterChart && siblingChart && masterChart.listAnswerModel && siblingChart.listAnswerModel){
      let i: number;
      siblingChart.typePercentageSelected = PercentageType.RELATIVE;
      if(masterChart.listAnswerModel.length > 0 && siblingChart.listAnswerModel.length > 0){
        for(i=0 ; i<masterChart.listAnswerModel.length && i<siblingChart.listAnswerModel.length ; i++){
          siblingChart.listAnswerModel[i].answerDiffPercShow = true;
          let masterPercentage = masterChart.listAnswerModel[i].answerPercentage;
          siblingChart.listAnswerModel[i].answerDiffPercentage = 0.0;
          let numerator = siblingChart.listAnswerModel[i].answerPercentage - masterPercentage;
          if(masterPercentage != 0){
            let diffPercentage = (numerator/masterPercentage)*100;
            siblingChart.listAnswerModel[i].answerDiffPercentage = Utils.roundNumber(diffPercentage,1);
          }else if(masterPercentage == 0 && numerator != 0){
            siblingChart.listAnswerModel[i].answerDiffPercShow = false;
            siblingChart.listAnswerModel[i].answerDiffPercentage = null;
          }
        }
      }else if(masterChart.nizPitanja && siblingChart.nizPitanja && masterChart.nizPitanja.length > 0 && siblingChart.nizPitanja.length > 0){
        for(i=0 ; i<masterChart.nizPitanja.length && i<siblingChart.nizPitanja.length ; i++){
          siblingChart.nizPitanja[i].answerDiffPercShow = true;
          let masterPercentage = masterChart.nizPitanja[i].answerPercentage;
          siblingChart.nizPitanja[i].answerDiffPercentage = 0.0;
          let numerator = siblingChart.nizPitanja[i].answerPercentage - masterPercentage;
          if(masterPercentage != 0){
            let diffPercentage = (numerator/masterPercentage)*100;
            siblingChart.nizPitanja[i].answerDiffPercentage = Utils.roundNumber(diffPercentage,1);
          } else if(masterPercentage == 0 && numerator != 0){
            siblingChart.nizPitanja[i].answerDiffPercShow = false;
            siblingChart.nizPitanja[i].answerDiffPercentage = null;
          }
        }
      }
    }

  }

  //This funciton will put the property answerDiffPercShow in false and answerDiffPercentage in null to reset this chart
  private resetChart(chart: MasterSummaryQuestionModel){
    if(chart && chart.listAnswerModel){
      let i: number;
      chart.typePercentageSelected = null;
      for(i=0 ; i<chart.listAnswerModel.length ; i++){
        chart.listAnswerModel[i].answerDiffPercShow = false;
        chart.listAnswerModel[i].answerDiffPercentage = null;
      }
      for(i=0 ; i<chart.nizPitanja.length ; i++){
        chart.nizPitanja[i].answerDiffPercShow = false;
        chart.nizPitanja[i].answerDiffPercentage = null;
      }
    }
  }

  //This function sets to true only the chart that has the diff percentage activated
  private changeChartSelection(reset:boolean){
    if(this.survMastSummPDModel){
      this.changeChartSelectionAllResponses(reset);
      this.changeChartSelectionOtherList(this.survMastSummPDModel.listSurveyCutQuestion,reset,true);
      this.changeChartSelectionOtherList(this.survMastSummPDModel.listSurveyVersionQuestion,reset,false);
    }
  }

  private changeChartSelectionAllResponses(reset:boolean){
    let i: number
    if(this.survMastSummPDModel.listQuestionModel){
      let questionIndex = this.isAllResponses ? this.chartJ : this.questionI;
      for(i=0;i<this.survMastSummPDModel.listQuestionModel.length;i++){
        if(reset){
          if(i == questionIndex && this.isAllResponses){
            this.survMastSummPDModel.listQuestionModel[i].diffPercentageSelected = false;
          }
        }else{
          if(i == questionIndex && this.isAllResponses){
            this.survMastSummPDModel.listQuestionModel[i].diffPercentageSelected = true;
          }else if(i == questionIndex && !this.isAllResponses){
            this.survMastSummPDModel.listQuestionModel[i].diffPercentageSelected = false;
          }
        }
      }
    }
  }

  private changeChartSelectionOtherList(chartList: SurveyVersionQuestionModel[] | SurveyCutQuestionModel[],reset:boolean,isCutList:boolean){
    let i: number;
    let j: number;
    if(chartList){
      let questionIndex = this.isAllResponses ? this.chartJ : this.questionI;
      for(i=0;i<chartList.length;i++){
        for(j=0;j<chartList[i].listQuestionModel.length;j++){
          if(i == this.chartJ && j == questionIndex && !this.isAllResponses && this.isCut == isCutList){
            chartList[i].listQuestionModel[j].diffPercentageSelected = false;
            if(!reset){
              chartList[i].listQuestionModel[j].diffPercentageSelected = true;
            }
          }else if(i == this.chartJ && j == questionIndex){
            chartList[i].listQuestionModel[j].diffPercentageSelected = false;
          }else if(i != this.chartJ && j == questionIndex){
            chartList[i].listQuestionModel[j].diffPercentageSelected = false;
          }
        }
      }
    }
  }

  private resetAllCharts(listQuestion: SurveyVersionQuestionModel[] | SurveyCutQuestionModel[],questionIndex: number){
      let i: number;
      let j: number;
      if(listQuestion){
        for(i=0;i<listQuestion.length;i++){
          for(j=0;j<listQuestion[i].listQuestionModel.length;j++){
            if(j == questionIndex){
              this.resetChart(listQuestion[i].listQuestionModel[j]);
            }
          }
        }
      }
  }

  private resetAllResponses(questionIndex: number){
    this.resetChart(this.survMastSummPDModel.listQuestionModel[questionIndex]);
  }

  private resetAllDataTurnOffIcon(){
    this.changeChartSelection(true);
    let questionIndex = this.isAllResponses ? this.chartJ : this.questionI;
    this.resetAllCharts(this.survMastSummPDModel.listSurveyCutQuestion,questionIndex);
    this.resetAllCharts(this.survMastSummPDModel.listSurveyVersionQuestion,questionIndex);
    this.resetAllResponses(questionIndex);
  }
}


// WEBPACK FOOTER //
// ./src/app/routes/surveys/master-summary/icon-dropdown/percentage-icon/percentage-icon.component.ts